/**
 * Campaign Knowledge Service
 * Builds and maintains a semantic knowledge base for AI advisor context
 *
 * This service pre-computes campaign summaries, funnel relationships,
 * audience overlaps, and performance patterns to enable token-efficient
 * AI interactions.
 */

import {
    normalizeObjective,
    evaluateCampaignHealth,
    extractMetricValue,
    calculateTrend,
    CAMPAIGN_OBJECTIVES,
    HEALTH_STATUS,
    formatCurrency
} from '../utils/objectiveHelpers';

// ===== Funnel Stage Classification =====

/**
 * Funnel stage definitions
 */
export const FUNNEL_STAGES = {
    TOFU: 'tofu',  // Top of Funnel - Awareness
    MOFU: 'mofu',  // Middle of Funnel - Consideration
    BOFU: 'bofu'   // Bottom of Funnel - Conversion
};

/**
 * Map objectives to funnel stages
 */
const OBJECTIVE_TO_FUNNEL = {
    [CAMPAIGN_OBJECTIVES.AWARENESS]: FUNNEL_STAGES.TOFU,
    [CAMPAIGN_OBJECTIVES.REACH]: FUNNEL_STAGES.TOFU,
    [CAMPAIGN_OBJECTIVES.BRAND_AWARENESS]: FUNNEL_STAGES.TOFU,
    [CAMPAIGN_OBJECTIVES.VIDEO_VIEWS]: FUNNEL_STAGES.TOFU,

    [CAMPAIGN_OBJECTIVES.TRAFFIC]: FUNNEL_STAGES.MOFU,
    [CAMPAIGN_OBJECTIVES.CLICKS]: FUNNEL_STAGES.MOFU,
    [CAMPAIGN_OBJECTIVES.ENGAGEMENT]: FUNNEL_STAGES.MOFU,
    [CAMPAIGN_OBJECTIVES.MESSAGES]: FUNNEL_STAGES.MOFU,
    [CAMPAIGN_OBJECTIVES.APP_INSTALLS]: FUNNEL_STAGES.MOFU,

    [CAMPAIGN_OBJECTIVES.CONVERSIONS]: FUNNEL_STAGES.BOFU,
    [CAMPAIGN_OBJECTIVES.SALES]: FUNNEL_STAGES.BOFU,
    [CAMPAIGN_OBJECTIVES.LEAD_GENERATION]: FUNNEL_STAGES.BOFU,
    [CAMPAIGN_OBJECTIVES.STORE_TRAFFIC]: FUNNEL_STAGES.BOFU
};

/**
 * Get funnel stage for a campaign
 */
export const getFunnelStage = (campaign) => {
    const objective = normalizeObjective(campaign.objective);
    return OBJECTIVE_TO_FUNNEL[objective] || FUNNEL_STAGES.MOFU;
};

/**
 * Check if campaign is awareness/TOFU stage
 */
export const isAwareness = (objective) => {
    const normalized = normalizeObjective(objective);
    return OBJECTIVE_TO_FUNNEL[normalized] === FUNNEL_STAGES.TOFU;
};

/**
 * Check if campaign is consideration/MOFU stage
 */
export const isConsideration = (objective) => {
    const normalized = normalizeObjective(objective);
    return OBJECTIVE_TO_FUNNEL[normalized] === FUNNEL_STAGES.MOFU;
};

/**
 * Check if campaign is conversion/BOFU stage
 */
export const isConversion = (objective) => {
    const normalized = normalizeObjective(objective);
    return OBJECTIVE_TO_FUNNEL[normalized] === FUNNEL_STAGES.BOFU;
};

// ===== Campaign Summarization =====

/**
 * Create a token-efficient campaign summary (~50 tokens vs 200+ for full data)
 *
 * IMPORTANT: Provides THREE distinct data sets clearly labeled:
 * 1. allTimeSpend: Lifetime total spend since campaign creation
 * 2. periodSpend: Amount spent during the selected date range only
 * 3. dailyBudget: Current daily budget allocation (what's scheduled/budgeted)
 *
 * Campaigns with $0 period spend are marked as "inactive due to $0 spend" to help
 * the AI understand these campaigns haven't been spending in the selected period.
 */
export const summarizeCampaign = (campaign) => {
    const periodMetrics = campaign.currentMetrics || campaign.insights?.data?.[0] || {};
    const allTimeMetrics = campaign.allTimeMetrics || {};
    const health = evaluateCampaignHealth(campaign);
    const stage = getFunnelStage(campaign);

    // === DATA SET 1: ALL-TIME (Lifetime total) ===
    const allTimeSpend = extractMetricValue(allTimeMetrics, 'spend') || 0;
    const allTimeConversions = extractMetricValue(allTimeMetrics, 'conversions') || 0;
    const allTimeRoas = extractMetricValue(allTimeMetrics, 'roas') || 0;

    // === DATA SET 2: SELECTED PERIOD (Date range) ===
    const periodSpend = extractMetricValue(periodMetrics, 'spend') || 0;
    const periodRoas = extractMetricValue(periodMetrics, 'roas') || 0;
    const periodConversions = extractMetricValue(periodMetrics, 'conversions') || 0;

    // === DATA SET 3: CURRENT ALLOCATION (Budget) ===
    const dailyBudget = campaign.budget?.daily || campaign.dailyBudget || campaign.daily_budget || 0;
    const lifetimeBudget = campaign.budget?.lifetime || campaign.lifetimeBudget || 0;

    // Determine spend status for AI context
    const hasZeroPeriodSpend = periodSpend === 0;
    const hasActiveBudget = dailyBudget > 0 || lifetimeBudget > 0;

    return {
        id: campaign.id || campaign.platformCampaignId,
        name: campaign.name,
        status: campaign.status?.toUpperCase() || 'UNKNOWN',
        stage,
        objective: normalizeObjective(campaign.objective),
        health: health.status,
        healthScore: health.score,
        primaryKPI: health.formattedValue || 'N/A',

        // ======= THREE DISTINCT DATA SETS =======

        // DATA SET 1: ALL-TIME METRICS (Lifetime total since campaign creation)
        allTime: {
            spend: Math.round(allTimeSpend),
            conversions: Math.round(allTimeConversions),
            roas: allTimeRoas ? allTimeRoas.toFixed(2) : null,
            _label: 'LIFETIME TOTAL - All spend since campaign creation'
        },

        // DATA SET 2: SELECTED PERIOD METRICS (Within selected date range only)
        selectedPeriod: {
            spend: Math.round(periodSpend),
            conversions: Math.round(periodConversions),
            roas: periodRoas ? periodRoas.toFixed(2) : null,
            _label: 'SELECTED PERIOD - Spend within the selected date range only'
        },

        // DATA SET 3: CURRENT BUDGET ALLOCATION (Scheduled/budgeted spend)
        currentAllocation: {
            dailyBudget: dailyBudget,
            lifetimeBudget: lifetimeBudget,
            _label: 'CURRENT ALLOCATION - What is scheduled/budgeted (not historical)'
        },

        // ======= LEGACY FIELDS (for backwards compatibility) =======
        periodSpend: Math.round(periodSpend),
        spend: Math.round(periodSpend),
        roas: periodRoas ? periodRoas.toFixed(2) : null,
        conversions: Math.round(periodConversions),
        dailyBudget: dailyBudget,

        // ======= STATUS FLAGS =======
        hasActiveBudget: hasActiveBudget,
        hasZeroPeriodSpend: hasZeroPeriodSpend,
        // Legacy field
        hasZeroSpend: hasZeroPeriodSpend,
        // Spend status for AI context
        spendStatus: hasZeroPeriodSpend
            ? 'inactive_zero_spend'
            : (periodSpend < dailyBudget ? 'low_spend' : 'active'),
        spendStatusNote: hasZeroPeriodSpend
            ? 'This campaign has $0 spend in the selected period - it may be inactive or just started. Check allTime.spend for lifetime total.'
            : null,
        trend: campaign.trends?.roas?.direction || 'stable'
    };
};

/**
 * Create minimal campaign reference (for lists)
 */
export const createCampaignRef = (campaign) => ({
    id: campaign.id,
    name: campaign.name,
    health: evaluateCampaignHealth(campaign).status
});

// ===== Funnel Analysis =====

/**
 * Build funnel structure from campaigns
 *
 * Now includes all THREE data sets per funnel stage:
 * 1. allTimeSpend - Lifetime total since campaign creation
 * 2. periodSpend - What was spent in the selected date range
 * 3. dailyBudget - Current daily budget allocation
 */
export const buildFunnelStructure = (campaigns) => {
    const activeCampaigns = campaigns.filter(c =>
        c.status?.toUpperCase() === 'ACTIVE'
    );

    const tofu = activeCampaigns.filter(c => getFunnelStage(c) === FUNNEL_STAGES.TOFU);
    const mofu = activeCampaigns.filter(c => getFunnelStage(c) === FUNNEL_STAGES.MOFU);
    const bofu = activeCampaigns.filter(c => getFunnelStage(c) === FUNNEL_STAGES.BOFU);

    // Calculate aggregates per stage - now includes all 3 data sets
    const aggregateStage = (stageCampaigns) => {
        const metrics = stageCampaigns.reduce((acc, c) => {
            const periodMetrics = c.currentMetrics || c.insights?.data?.[0] || {};
            const allTimeMetrics = c.allTimeMetrics || {};

            // DATA SET 1: All-Time (Lifetime)
            acc.allTimeSpend += extractMetricValue(allTimeMetrics, 'spend') || 0;
            acc.allTimeConversions += extractMetricValue(allTimeMetrics, 'conversions') || 0;
            acc.allTimeRevenue += extractMetricValue(allTimeMetrics, 'revenue') || 0;

            // DATA SET 2: Selected Period
            acc.periodSpend += extractMetricValue(periodMetrics, 'spend') || 0;
            // Keep legacy spend field for backwards compatibility
            acc.spend += extractMetricValue(periodMetrics, 'spend') || 0;
            acc.revenue += extractMetricValue(periodMetrics, 'revenue') || 0;
            acc.impressions += extractMetricValue(periodMetrics, 'impressions') || 0;
            acc.clicks += extractMetricValue(periodMetrics, 'clicks') || 0;
            acc.conversions += extractMetricValue(periodMetrics, 'conversions') || 0;

            // DATA SET 3: Current Allocation (Budget)
            acc.totalDailyBudget += c.budget?.daily || c.dailyBudget || c.daily_budget || 0;
            return acc;
        }, {
            // All-time totals
            allTimeSpend: 0, allTimeConversions: 0, allTimeRevenue: 0,
            // Period totals
            periodSpend: 0, spend: 0, revenue: 0, impressions: 0, clicks: 0, conversions: 0,
            // Budget allocation
            totalDailyBudget: 0
        });

        metrics.roas = metrics.spend > 0 ? metrics.revenue / metrics.spend : 0;
        metrics.allTimeRoas = metrics.allTimeSpend > 0 ? metrics.allTimeRevenue / metrics.allTimeSpend : 0;
        return metrics;
    };

    const tofuMetrics = aggregateStage(tofu);
    const mofuMetrics = aggregateStage(mofu);
    const bofuMetrics = aggregateStage(bofu);

    // Determine health status for each stage
    const getStageHealth = (stageCampaigns) => {
        if (stageCampaigns.length === 0) return 'empty';
        const healthScores = stageCampaigns.map(c => evaluateCampaignHealth(c).score);
        const avgScore = healthScores.reduce((a, b) => a + b, 0) / healthScores.length;
        if (avgScore >= 75) return 'healthy';
        if (avgScore >= 50) return 'warning';
        return 'critical';
    };

    return {
        tofu: {
            campaigns: tofu.map(summarizeCampaign),
            count: tofu.length,
            metrics: tofuMetrics,
            health: getStageHealth(tofu),
            spendShare: 0, // Calculated later (based on period spend)
            budgetShare: 0 // Calculated later (based on daily budget allocation)
        },
        mofu: {
            campaigns: mofu.map(summarizeCampaign),
            count: mofu.length,
            metrics: mofuMetrics,
            health: getStageHealth(mofu),
            spendShare: 0,
            budgetShare: 0
        },
        bofu: {
            campaigns: bofu.map(summarizeCampaign),
            count: bofu.length,
            metrics: bofuMetrics,
            health: getStageHealth(bofu),
            spendShare: 0,
            budgetShare: 0
        }
    };
};

/**
 * Analyze funnel health and detect issues
 *
 * Now considers both period spend (historical) and daily budget allocation (current)
 * to provide more accurate recommendations.
 */
export const analyzeFunnelHealth = (funnelStructure) => {
    const { tofu, mofu, bofu } = funnelStructure;
    const issues = [];
    const opportunities = [];

    const totalPeriodSpend = tofu.metrics.spend + mofu.metrics.spend + bofu.metrics.spend;
    const totalDailyBudget = (tofu.metrics.totalDailyBudget || 0) +
                            (mofu.metrics.totalDailyBudget || 0) +
                            (bofu.metrics.totalDailyBudget || 0);

    // Calculate spend shares (based on period/historical spend)
    if (totalPeriodSpend > 0) {
        funnelStructure.tofu.spendShare = Math.round((tofu.metrics.spend / totalPeriodSpend) * 100);
        funnelStructure.mofu.spendShare = Math.round((mofu.metrics.spend / totalPeriodSpend) * 100);
        funnelStructure.bofu.spendShare = Math.round((bofu.metrics.spend / totalPeriodSpend) * 100);
    }

    // Calculate budget shares (based on current daily budget allocation)
    if (totalDailyBudget > 0) {
        funnelStructure.tofu.budgetShare = Math.round(((tofu.metrics.totalDailyBudget || 0) / totalDailyBudget) * 100);
        funnelStructure.mofu.budgetShare = Math.round(((mofu.metrics.totalDailyBudget || 0) / totalDailyBudget) * 100);
        funnelStructure.bofu.budgetShare = Math.round(((bofu.metrics.totalDailyBudget || 0) / totalDailyBudget) * 100);
    }

    // Check for missing funnel stages
    if (tofu.count === 0 && (mofu.count > 0 || bofu.count > 0)) {
        issues.push({
            type: 'MISSING_TOFU',
            severity: 'high',
            message: 'No active awareness campaigns. Your funnel lacks top-of-funnel traffic to feed consideration and conversion stages.',
            impact: 'Retargeting pools will shrink over time, reducing BOFU campaign effectiveness.',
            recommendation: 'Launch awareness or reach campaigns to build your audience pipeline.'
        });
    }

    if (mofu.count === 0 && bofu.count > 0) {
        issues.push({
            type: 'MISSING_MOFU',
            severity: 'medium',
            message: 'No active consideration campaigns. Users jump directly from awareness to conversion.',
            impact: 'Higher CPA as cold audiences are pushed to convert without nurturing.',
            recommendation: 'Add engagement or traffic campaigns to warm up audiences before conversion push.'
        });
    }

    if (bofu.count === 0 && (tofu.count > 0 || mofu.count > 0)) {
        issues.push({
            type: 'MISSING_BOFU',
            severity: 'high',
            message: 'No active conversion campaigns. You\'re building audiences but not converting them.',
            impact: 'Awareness spend is not being monetized. Warm audiences may go cold.',
            recommendation: 'Launch conversion or sales campaigns targeting engaged audiences.'
        });
    }

    // Check funnel balance (ideal ratio roughly 40% TOFU, 30% MOFU, 30% BOFU)
    if (totalPeriodSpend > 0) {
        if (funnelStructure.bofu.spendShare > 70) {
            issues.push({
                type: 'BOFU_HEAVY',
                severity: 'medium',
                message: `${funnelStructure.bofu.spendShare}% of spend is on conversion campaigns.`,
                impact: 'Over-reliance on BOFU can exhaust retargeting audiences and increase CPAs over time.',
                recommendation: 'Allocate more budget to TOFU/MOFU to replenish your audience pool.'
            });
        }

        if (funnelStructure.tofu.spendShare > 60 && bofu.metrics.roas < 1.5) {
            issues.push({
                type: 'TOFU_HEAVY_LOW_ROAS',
                severity: 'medium',
                message: `${funnelStructure.tofu.spendShare}% of spend is on awareness but BOFU ROAS is only ${bofu.metrics.roas.toFixed(2)}x.`,
                impact: 'Heavy awareness spend isn\'t translating to efficient conversions.',
                recommendation: 'Review audience quality from TOFU campaigns and optimize BOFU targeting.'
            });
        }
    }

    // Check for opportunities
    if (bofu.metrics.roas > 3 && bofu.count > 0) {
        opportunities.push({
            type: 'SCALE_BOFU',
            message: `BOFU campaigns achieving ${bofu.metrics.roas.toFixed(2)}x ROAS - strong scaling opportunity.`,
            recommendation: 'Increase BOFU budgets and ensure TOFU/MOFU are feeding the funnel.'
        });
    }

    if (tofu.count > 0 && tofu.health === 'healthy' && bofu.count === 0) {
        opportunities.push({
            type: 'MONETIZE_TOFU',
            message: 'Healthy awareness campaigns building audience without conversion campaigns.',
            recommendation: 'Launch retargeting campaigns to monetize your warmed-up audiences.'
        });
    }

    // Calculate total daily budget for context
    const totalDailyBudgetCalc = (tofu.metrics.totalDailyBudget || 0) +
                                 (mofu.metrics.totalDailyBudget || 0) +
                                 (bofu.metrics.totalDailyBudget || 0);

    return {
        summary: buildFunnelSummary(funnelStructure),
        issues,
        opportunities,
        isHealthy: issues.filter(i => i.severity === 'high').length === 0,
        // Period spend distribution (historical - what was actually spent)
        spendDistribution: {
            tofu: funnelStructure.tofu.spendShare,
            mofu: funnelStructure.mofu.spendShare,
            bofu: funnelStructure.bofu.spendShare
        },
        // Current budget allocation (what's actively budgeted per day)
        budgetDistribution: {
            tofu: funnelStructure.tofu.budgetShare,
            mofu: funnelStructure.mofu.budgetShare,
            bofu: funnelStructure.bofu.budgetShare
        },
        // Total daily budget across all stages
        totalDailyBudget: totalDailyBudgetCalc
    };
};

/**
 * Build human-readable funnel summary
 * Now includes all THREE data sets clearly labeled
 */
const buildFunnelSummary = (funnelStructure) => {
    const { tofu, mofu, bofu } = funnelStructure;
    const parts = [];

    parts.push(`Funnel: ${tofu.count} awareness → ${mofu.count} consideration → ${bofu.count} conversion campaigns`);

    if (tofu.count + mofu.count + bofu.count === 0) {
        return 'No active campaigns in the funnel.';
    }

    // DATA SET 1: All-Time Spend (Lifetime total)
    const totalAllTimeSpend = (tofu.metrics.allTimeSpend || 0) +
                              (mofu.metrics.allTimeSpend || 0) +
                              (bofu.metrics.allTimeSpend || 0);
    if (totalAllTimeSpend > 0) {
        parts.push(`ALL-TIME SPEND (lifetime): ${formatCurrency(tofu.metrics.allTimeSpend || 0)} TOFU / ${formatCurrency(mofu.metrics.allTimeSpend || 0)} MOFU / ${formatCurrency(bofu.metrics.allTimeSpend || 0)} BOFU`);
    }

    // DATA SET 2: Period Spend (Selected date range)
    const totalPeriodSpend = tofu.metrics.spend + mofu.metrics.spend + bofu.metrics.spend;
    if (totalPeriodSpend > 0) {
        parts.push(`SELECTED PERIOD SPEND: ${formatCurrency(tofu.metrics.spend)} TOFU / ${formatCurrency(mofu.metrics.spend)} MOFU / ${formatCurrency(bofu.metrics.spend)} BOFU`);
    }

    // DATA SET 3: Current Daily Budget Allocation
    const totalDailyBudget = (tofu.metrics.totalDailyBudget || 0) +
                            (mofu.metrics.totalDailyBudget || 0) +
                            (bofu.metrics.totalDailyBudget || 0);
    if (totalDailyBudget > 0) {
        parts.push(`CURRENT DAILY BUDGET: ${formatCurrency(tofu.metrics.totalDailyBudget || 0)} TOFU / ${formatCurrency(mofu.metrics.totalDailyBudget || 0)} MOFU / ${formatCurrency(bofu.metrics.totalDailyBudget || 0)} BOFU`);
    }

    if (bofu.metrics.roas > 0) {
        parts.push(`BOFU ROAS (period): ${bofu.metrics.roas.toFixed(2)}x`);
    }

    return parts.join('. ');
};

/**
 * Detect funnel dependencies and relationships
 */
export const detectFunnelRelationships = (campaigns) => {
    const relationships = [];
    const funnelStructure = buildFunnelStructure(campaigns);
    const { tofu, mofu, bofu } = funnelStructure;

    // Detect retargeting dependencies
    const bofuCampaigns = campaigns.filter(c =>
        c.status?.toUpperCase() === 'ACTIVE' &&
        getFunnelStage(c) === FUNNEL_STAGES.BOFU
    );

    const tofuMofuCampaigns = campaigns.filter(c =>
        c.status?.toUpperCase() === 'ACTIVE' &&
        [FUNNEL_STAGES.TOFU, FUNNEL_STAGES.MOFU].includes(getFunnelStage(c))
    );

    if (bofuCampaigns.length > 0 && tofuMofuCampaigns.length > 0) {
        relationships.push({
            type: 'RETARGETING_DEPENDENCY',
            upstream: tofuMofuCampaigns.map(c => c.name),
            downstream: bofuCampaigns.map(c => c.name),
            description: 'BOFU campaigns likely retarget audiences from TOFU/MOFU campaigns.',
            impact: 'Pausing upstream campaigns will reduce downstream audience pool within 7-14 days.',
            riskLevel: 'high'
        });
    }

    // Detect potential audience flow
    if (tofu.count > 0 && mofu.count > 0) {
        relationships.push({
            type: 'AUDIENCE_FLOW',
            from: 'TOFU',
            to: 'MOFU',
            description: 'Awareness campaigns feed consideration stage audiences.',
            healthyFlow: tofu.metrics.impressions > mofu.metrics.impressions * 0.5
        });
    }

    if (mofu.count > 0 && bofu.count > 0) {
        relationships.push({
            type: 'AUDIENCE_FLOW',
            from: 'MOFU',
            to: 'BOFU',
            description: 'Consideration campaigns nurture audiences for conversion.',
            healthyFlow: mofu.metrics.clicks > bofu.metrics.conversions
        });
    }

    // Summary relationship
    relationships.push({
        type: 'FUNNEL_STRUCTURE',
        summary: `${tofu.count} TOFU → ${mofu.count} MOFU → ${bofu.count} BOFU`,
        totalActive: tofu.count + mofu.count + bofu.count,
        balanceScore: calculateFunnelBalance(funnelStructure)
    });

    return relationships;
};

/**
 * Calculate funnel balance score (0-100)
 */
const calculateFunnelBalance = (funnelStructure) => {
    const { tofu, mofu, bofu } = funnelStructure;
    const total = tofu.count + mofu.count + bofu.count;

    if (total === 0) return 0;

    // Ideal distribution: 40% TOFU, 30% MOFU, 30% BOFU
    const tofuRatio = tofu.count / total;
    const mofuRatio = mofu.count / total;
    const bofuRatio = bofu.count / total;

    // Score based on deviation from ideal
    const tofuScore = Math.max(0, 100 - Math.abs(tofuRatio - 0.4) * 200);
    const mofuScore = Math.max(0, 100 - Math.abs(mofuRatio - 0.3) * 200);
    const bofuScore = Math.max(0, 100 - Math.abs(bofuRatio - 0.3) * 200);

    return Math.round((tofuScore + mofuScore + bofuScore) / 3);
};

// ===== Performance Analysis =====

/**
 * Aggregate performance by objective type
 */
export const aggregateByObjective = (campaigns, stage) => {
    const stageCampaigns = campaigns.filter(c => {
        const funnelStage = getFunnelStage(c);
        if (stage === 'awareness') return funnelStage === FUNNEL_STAGES.TOFU;
        if (stage === 'consideration') return funnelStage === FUNNEL_STAGES.MOFU;
        if (stage === 'conversion') return funnelStage === FUNNEL_STAGES.BOFU;
        return false;
    });

    if (stageCampaigns.length === 0) {
        return {
            count: 0,
            spend: 0,
            campaigns: [],
            health: 'empty'
        };
    }

    const totalSpend = stageCampaigns.reduce((sum, c) => {
        const metrics = c.currentMetrics || c.insights?.data?.[0] || {};
        return sum + (extractMetricValue(metrics, 'spend') || 0);
    }, 0);

    const totalRevenue = stageCampaigns.reduce((sum, c) => {
        const metrics = c.currentMetrics || c.insights?.data?.[0] || {};
        return sum + (extractMetricValue(metrics, 'revenue') || 0);
    }, 0);

    const avgHealth = stageCampaigns.reduce((sum, c) => {
        return sum + evaluateCampaignHealth(c).score;
    }, 0) / stageCampaigns.length;

    return {
        count: stageCampaigns.length,
        spend: Math.round(totalSpend),
        revenue: Math.round(totalRevenue),
        roas: totalSpend > 0 ? (totalRevenue / totalSpend).toFixed(2) : '0',
        campaigns: stageCampaigns.map(createCampaignRef),
        health: avgHealth >= 75 ? 'healthy' : avgHealth >= 50 ? 'warning' : 'critical',
        avgHealthScore: Math.round(avgHealth)
    };
};

// ===== Budget Analysis =====

/**
 * Analyze budget distribution across campaigns
 *
 * IMPORTANT: Now provides THREE distinct data sets:
 * 1. allTimeSpend: Lifetime total spend since campaign creation
 * 2. periodSpend: Amount spent during the selected date range (historical)
 * 3. dailyBudget: Current daily budget allocation (what's scheduled)
 *
 * This is critical for accurate AI recommendations. A campaign with $50/day budget
 * but $0 period spend is a NEW or recently UNPAUSED campaign, not one to reallocate from.
 */
export const analyzeBudgetDistribution = (campaigns) => {
    const activeCampaigns = campaigns.filter(c => c.status?.toUpperCase() === 'ACTIVE');

    if (activeCampaigns.length === 0) {
        return {
            totalAllTimeSpend: 0,
            totalDailyBudget: 0,
            totalPeriodSpend: 0,
            campaigns: [],
            distribution: { tofu: 0, mofu: 0, bofu: 0 },
            topSpenders: [],
            topBudgetAllocations: [],
            recommendations: [],
            zeroSpendCampaigns: []
        };
    }

    const budgets = activeCampaigns.map(c => {
        const periodMetrics = c.currentMetrics || c.insights?.data?.[0] || {};
        const allTimeMetrics = c.allTimeMetrics || {};
        const dailyBudget = c.budget?.daily || c.dailyBudget || c.daily_budget || 0;
        const periodSpend = extractMetricValue(periodMetrics, 'spend') || 0;
        const allTimeSpend = extractMetricValue(allTimeMetrics, 'spend') || 0;
        const hasZeroPeriodSpend = periodSpend === 0;

        return {
            id: c.id || c.platformCampaignId,
            name: c.name,
            stage: getFunnelStage(c),
            // DATA SET 1: All-Time (Lifetime)
            allTimeSpend: allTimeSpend,
            // DATA SET 2: Selected Period
            periodSpend: periodSpend,
            // DATA SET 3: Current Allocation
            dailyBudget: dailyBudget,
            health: evaluateCampaignHealth(c).status,
            roas: extractMetricValue(periodMetrics, 'roas') || 0,
            hasActiveBudget: dailyBudget > 0,
            hasZeroPeriodSpend: hasZeroPeriodSpend,
            // Legacy field
            hasZeroSpend: hasZeroPeriodSpend,
            // Spend status for AI context
            spendStatus: hasZeroPeriodSpend
                ? 'inactive_zero_spend'
                : (periodSpend < dailyBudget ? 'low_spend' : 'active')
        };
    });

    const totalBudget = budgets.reduce((sum, b) => sum + b.dailyBudget, 0);
    const totalPeriodSpend = budgets.reduce((sum, b) => sum + b.periodSpend, 0);
    const totalAllTimeSpend = budgets.reduce((sum, b) => sum + b.allTimeSpend, 0);

    // Group by stage - all 3 data sets
    const budgetByStage = {
        tofu: budgets.filter(b => b.stage === FUNNEL_STAGES.TOFU).reduce((s, b) => s + b.dailyBudget, 0),
        mofu: budgets.filter(b => b.stage === FUNNEL_STAGES.MOFU).reduce((s, b) => s + b.dailyBudget, 0),
        bofu: budgets.filter(b => b.stage === FUNNEL_STAGES.BOFU).reduce((s, b) => s + b.dailyBudget, 0)
    };

    const periodSpendByStage = {
        tofu: budgets.filter(b => b.stage === FUNNEL_STAGES.TOFU).reduce((s, b) => s + b.periodSpend, 0),
        mofu: budgets.filter(b => b.stage === FUNNEL_STAGES.MOFU).reduce((s, b) => s + b.periodSpend, 0),
        bofu: budgets.filter(b => b.stage === FUNNEL_STAGES.BOFU).reduce((s, b) => s + b.periodSpend, 0)
    };

    const allTimeSpendByStage = {
        tofu: budgets.filter(b => b.stage === FUNNEL_STAGES.TOFU).reduce((s, b) => s + b.allTimeSpend, 0),
        mofu: budgets.filter(b => b.stage === FUNNEL_STAGES.MOFU).reduce((s, b) => s + b.allTimeSpend, 0),
        bofu: budgets.filter(b => b.stage === FUNNEL_STAGES.BOFU).reduce((s, b) => s + b.allTimeSpend, 0)
    };

    // Find top budget allocations (current daily budgets)
    const topBudgetAllocations = [...budgets]
        .sort((a, b) => b.dailyBudget - a.dailyBudget)
        .slice(0, 5)
        .map(b => ({
            name: b.name,
            allTimeSpend: formatCurrency(b.allTimeSpend),
            periodSpend: formatCurrency(b.periodSpend),
            dailyBudget: formatCurrency(b.dailyBudget),
            health: b.health,
            share: totalBudget > 0 ? Math.round((b.dailyBudget / totalBudget) * 100) : 0
        }));

    // Find top all-time spenders (lifetime)
    const topAllTimeSpenders = [...budgets]
        .sort((a, b) => b.allTimeSpend - a.allTimeSpend)
        .slice(0, 5)
        .map(b => ({
            name: b.name,
            allTimeSpend: formatCurrency(b.allTimeSpend),
            periodSpend: formatCurrency(b.periodSpend),
            dailyBudget: formatCurrency(b.dailyBudget),
            health: b.health,
            share: totalAllTimeSpend > 0 ? Math.round((b.allTimeSpend / totalAllTimeSpend) * 100) : 0
        }));

    // Find top period spenders (selected date range)
    const topPeriodSpenders = [...budgets]
        .sort((a, b) => b.periodSpend - a.periodSpend)
        .slice(0, 5)
        .map(b => ({
            name: b.name,
            allTimeSpend: formatCurrency(b.allTimeSpend),
            periodSpend: formatCurrency(b.periodSpend),
            dailyBudget: formatCurrency(b.dailyBudget),
            health: b.health,
            share: totalPeriodSpend > 0 ? Math.round((b.periodSpend / totalPeriodSpend) * 100) : 0
        }));

    // Legacy alias
    const topSpenders = topPeriodSpenders;

    // Identify campaigns with zero period spend (inactive in this period)
    const zeroSpendCampaigns = budgets
        .filter(b => b.hasZeroPeriodSpend)
        .map(b => ({
            name: b.name,
            allTimeSpend: formatCurrency(b.allTimeSpend),
            periodSpend: formatCurrency(b.periodSpend),
            dailyBudget: formatCurrency(b.dailyBudget),
            stage: b.stage,
            spendStatus: 'inactive_zero_spend',
            note: 'This campaign has $0 spend in the selected period - it may be inactive or just started. Check allTimeSpend for lifetime total.'
        }));

    // Generate recommendations
    const recommendations = [];

    // Find underperforming high-budget campaigns
    const highBudgetLowPerformers = budgets.filter(b =>
        b.dailyBudget > totalBudget * 0.15 &&
        b.health === HEALTH_STATUS.CRITICAL
    );

    if (highBudgetLowPerformers.length > 0) {
        recommendations.push({
            type: 'REDUCE_WASTE',
            message: `${highBudgetLowPerformers.length} high-budget campaign(s) are underperforming`,
            campaigns: highBudgetLowPerformers.map(b => b.name),
            action: 'Consider reducing budgets or pausing to investigate'
        });
    }

    // Find high-performing campaigns that could scale
    const scaleOpportunities = budgets.filter(b =>
        b.health === HEALTH_STATUS.EXCELLENT &&
        b.dailyBudget < totalBudget * 0.2
    );

    if (scaleOpportunities.length > 0) {
        recommendations.push({
            type: 'SCALE_WINNERS',
            message: `${scaleOpportunities.length} high-performing campaign(s) have room to scale`,
            campaigns: scaleOpportunities.map(b => b.name),
            action: 'Consider increasing budgets by 20-50%'
        });
    }

    return {
        // ======= THREE DISTINCT TOTALS =======

        // DATA SET 1: ALL-TIME SPEND (Lifetime total)
        totalAllTimeSpend: Math.round(totalAllTimeSpend),
        totalAllTimeSpend_label: 'LIFETIME TOTAL - All spend since campaign creation',

        // DATA SET 2: SELECTED PERIOD SPEND (Date range)
        totalPeriodSpend: Math.round(totalPeriodSpend),
        totalPeriodSpend_label: 'SELECTED PERIOD - Spend within the selected date range only',

        // DATA SET 3: CURRENT DAILY BUDGET (Scheduled allocation)
        totalDailyBudget: Math.round(totalBudget),
        totalDailyBudget_label: 'CURRENT ALLOCATION - What is scheduled/budgeted per day',

        // ======= DISTRIBUTION BY FUNNEL STAGE =======

        // All-Time Spend by stage (lifetime)
        allTimeSpendByStage: {
            tofu: Math.round(allTimeSpendByStage.tofu),
            mofu: Math.round(allTimeSpendByStage.mofu),
            bofu: Math.round(allTimeSpendByStage.bofu),
            _label: 'LIFETIME TOTAL by funnel stage'
        },

        // Period spend by stage (selected date range)
        periodSpendByStage: {
            tofu: Math.round(periodSpendByStage.tofu),
            mofu: Math.round(periodSpendByStage.mofu),
            bofu: Math.round(periodSpendByStage.bofu),
            _label: 'SELECTED PERIOD by funnel stage'
        },

        // Daily budget by stage (current allocation)
        budgetByStage: {
            tofu: Math.round(budgetByStage.tofu),
            mofu: Math.round(budgetByStage.mofu),
            bofu: Math.round(budgetByStage.bofu),
            _label: 'CURRENT DAILY BUDGET by funnel stage'
        },

        // Budget percentage distribution
        budgetDistributionPercent: {
            tofu: totalBudget > 0 ? Math.round((budgetByStage.tofu / totalBudget) * 100) : 0,
            mofu: totalBudget > 0 ? Math.round((budgetByStage.mofu / totalBudget) * 100) : 0,
            bofu: totalBudget > 0 ? Math.round((budgetByStage.bofu / totalBudget) * 100) : 0
        },

        // Legacy fields for backwards compatibility
        distribution: {
            tofu: Math.round(budgetByStage.tofu),
            mofu: Math.round(budgetByStage.mofu),
            bofu: Math.round(budgetByStage.bofu)
        },
        distributionPercent: {
            tofu: totalBudget > 0 ? Math.round((budgetByStage.tofu / totalBudget) * 100) : 0,
            mofu: totalBudget > 0 ? Math.round((budgetByStage.mofu / totalBudget) * 100) : 0,
            bofu: totalBudget > 0 ? Math.round((budgetByStage.bofu / totalBudget) * 100) : 0
        },
        periodSpendDistribution: {
            tofu: Math.round(periodSpendByStage.tofu),
            mofu: Math.round(periodSpendByStage.mofu),
            bofu: Math.round(periodSpendByStage.bofu)
        },

        // ======= TOP CAMPAIGNS =======

        // Top by all-time spend (lifetime)
        topAllTimeSpenders,
        // Top by period spend (selected date range)
        topPeriodSpenders,
        // Top by daily budget allocation (current)
        topBudgetAllocations,
        // Legacy alias
        topSpenders,

        // ======= ZERO SPEND CAMPAIGNS =======
        // IMPORTANT: These campaigns have $0 spend in the selected period
        // They may be inactive or just started - check allTimeSpend for lifetime total
        zeroSpendCampaigns,

        recommendations
    };
};

// ===== Main Knowledge Base Builder =====

/**
 * Build the complete campaign knowledge base
 * This is the main entry point for generating AI context
 */
export const buildCampaignKnowledgeBase = (campaigns, previousPeriodData = {}) => {
    if (!campaigns || campaigns.length === 0) {
        return {
            isEmpty: true,
            funnel: null,
            performanceByStage: null,
            budgetAnalysis: null,
            relationships: [],
            summary: 'No campaign data available.'
        };
    }

    // Build funnel structure
    const funnelStructure = buildFunnelStructure(campaigns);
    const funnelAnalysis = analyzeFunnelHealth(funnelStructure);
    const relationships = detectFunnelRelationships(campaigns);

    // Build performance summaries
    const performanceByStage = {
        awareness: aggregateByObjective(campaigns, 'awareness'),
        consideration: aggregateByObjective(campaigns, 'consideration'),
        conversion: aggregateByObjective(campaigns, 'conversion')
    };

    // Budget analysis
    const budgetAnalysis = analyzeBudgetDistribution(campaigns);

    // Calculate trends if previous data available
    const trends = {};
    if (Object.keys(previousPeriodData).length > 0) {
        campaigns.forEach(c => {
            const current = c.currentMetrics || c.insights?.data?.[0] || {};
            const previous = previousPeriodData[c.id] || {};

            const currentSpend = extractMetricValue(current, 'spend') || 0;
            const previousSpend = extractMetricValue(previous, 'spend') || 0;
            const currentRoas = extractMetricValue(current, 'roas') || 0;
            const previousRoas = extractMetricValue(previous, 'roas') || 0;

            if (previousSpend > 0 || previousRoas > 0) {
                trends[c.id] = {
                    spend: calculateTrend(currentSpend, previousSpend),
                    roas: calculateTrend(currentRoas, previousRoas)
                };
            }
        });
    }

    // Build overall summary
    const activeCampaigns = campaigns.filter(c => c.status?.toUpperCase() === 'ACTIVE');

    // DATA SET 1: All-Time Spend (Lifetime total)
    const totalAllTimeSpend = campaigns.reduce((sum, c) => {
        const m = c.allTimeMetrics || {};
        return sum + (extractMetricValue(m, 'spend') || 0);
    }, 0);

    const totalAllTimeRevenue = campaigns.reduce((sum, c) => {
        const m = c.allTimeMetrics || {};
        return sum + (extractMetricValue(m, 'revenue') || 0);
    }, 0);

    // DATA SET 2: Period Spend (Selected date range)
    const totalPeriodSpend = campaigns.reduce((sum, c) => {
        const m = c.currentMetrics || c.insights?.data?.[0] || {};
        return sum + (extractMetricValue(m, 'spend') || 0);
    }, 0);

    const totalPeriodRevenue = campaigns.reduce((sum, c) => {
        const m = c.currentMetrics || c.insights?.data?.[0] || {};
        return sum + (extractMetricValue(m, 'revenue') || 0);
    }, 0);

    // DATA SET 3: Current Daily Budget (Scheduled allocation)
    const totalDailyBudget = activeCampaigns.reduce((sum, c) => {
        return sum + (c.budget?.daily || c.dailyBudget || c.daily_budget || 0);
    }, 0);

    // Count campaigns with $0 period spend (inactive in this period)
    const zeroSpendCount = activeCampaigns.filter(c => {
        const m = c.currentMetrics || c.insights?.data?.[0] || {};
        const periodSpend = extractMetricValue(m, 'spend') || 0;
        return periodSpend === 0;
    }).length;

    return {
        isEmpty: false,
        generatedAt: new Date().toISOString(),

        // High-level summary with THREE distinct data sets clearly labeled
        summary: {
            totalCampaigns: campaigns.length,
            activeCampaigns: activeCampaigns.length,

            // ======= THREE DISTINCT DATA SETS =======

            // DATA SET 1: ALL-TIME (Lifetime total since campaign creation)
            allTime: {
                spend: Math.round(totalAllTimeSpend),
                revenue: Math.round(totalAllTimeRevenue),
                roas: totalAllTimeSpend > 0 ? (totalAllTimeRevenue / totalAllTimeSpend).toFixed(2) : '0',
                _label: 'LIFETIME TOTAL - All spend/revenue since campaign creation'
            },

            // DATA SET 2: SELECTED PERIOD (Within selected date range only)
            selectedPeriod: {
                spend: Math.round(totalPeriodSpend),
                revenue: Math.round(totalPeriodRevenue),
                roas: totalPeriodSpend > 0 ? (totalPeriodRevenue / totalPeriodSpend).toFixed(2) : '0',
                _label: 'SELECTED PERIOD - Spend/revenue within the selected date range only'
            },

            // DATA SET 3: CURRENT ALLOCATION (Scheduled/budgeted spend per day)
            currentAllocation: {
                dailyBudget: Math.round(totalDailyBudget),
                _label: 'CURRENT ALLOCATION - What is scheduled/budgeted per day (not historical)'
            },

            // ======= LEGACY FIELDS (for backwards compatibility) =======
            totalPeriodSpend: Math.round(totalPeriodSpend),
            totalSpend: Math.round(totalPeriodSpend),
            totalDailyBudget: Math.round(totalDailyBudget),
            totalRevenue: Math.round(totalPeriodRevenue),
            overallRoas: totalPeriodSpend > 0 ? (totalPeriodRevenue / totalPeriodSpend).toFixed(2) : '0',

            // Count of campaigns with $0 period spend - these may be inactive or just started
            zeroSpendCampaigns: zeroSpendCount,
            zeroSpendNote: 'These campaigns have $0 spend in the selected period - check allTime.spend for lifetime total'
        },

        // Funnel structure and health
        funnel: {
            structure: funnelStructure,
            analysis: funnelAnalysis,
            relationships
        },

        // Performance by funnel stage
        performanceByStage,

        // Budget distribution
        budgetAnalysis,

        // Campaign trends (optional, if previous data available)
        trends: Object.keys(trends).length > 0 ? trends : null,

        // Quick campaign lookup
        campaignIndex: campaigns.reduce((acc, c) => {
            acc[c.id] = summarizeCampaign(c);
            return acc;
        }, {})
    };
};

/**
 * Get a specific section of the knowledge base
 * Used by the smart context builder to fetch only needed sections
 */
export const getKnowledgeSection = (knowledgeBase, path) => {
    if (!knowledgeBase || knowledgeBase.isEmpty) return null;

    const parts = path.split('.');
    let current = knowledgeBase;

    for (const part of parts) {
        if (current && typeof current === 'object' && part in current) {
            current = current[part];
        } else {
            return null;
        }
    }

    return current;
};

export default {
    // Funnel stages
    FUNNEL_STAGES,
    getFunnelStage,
    isAwareness,
    isConsideration,
    isConversion,

    // Summarization
    summarizeCampaign,
    createCampaignRef,

    // Funnel analysis
    buildFunnelStructure,
    analyzeFunnelHealth,
    detectFunnelRelationships,

    // Performance
    aggregateByObjective,

    // Budget
    analyzeBudgetDistribution,

    // Main builder
    buildCampaignKnowledgeBase,
    getKnowledgeSection
};
